import { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ChevronDown } from 'lucide-react';
import useActiveRole from '../hooks/useActiveRole';
import { switchActiveRole } from '../../../store/slices/rolesSlice';
import { PARTICIPANT_TYPES } from '../../../shared/utils/constants';

// Static color classes — Tailwind purges dynamic classes like `bg-${color}-500`
const DOT_COLORS = {
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
  red: 'bg-red-500',
  yellow: 'bg-yellow-500',
};

const RoleSelector = () => {
  const dispatch = useDispatch();
  const { activeRole, availableRoles } = useActiveRole();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!activeRole || availableRoles.length === 0) return null;

  const current = PARTICIPANT_TYPES[activeRole];
  if (!current) return null;

  /* Switching a role changes who you are in the workflow — which stages you can
     be assigned, what shows as yours — not which screen you are on. It used to
     navigate to a per-role workspace; those are gone, and the work happens in
     the workflow either way. */
  const handleSwitch = (roleKey) => {
    dispatch(switchActiveRole(roleKey));
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => availableRoles.length > 1 && setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-card hover:bg-card-hover text-sm transition-colors border border-border"
      >
        <span className={`w-2 h-2 rounded-full ${DOT_COLORS[current.color] || 'bg-gray-500'}`} />
        <span className="text-foreground">{current.label}</span>
        {availableRoles.length > 1 && <ChevronDown className="w-3.5 h-3.5 text-muted" />}
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-60 bg-card rounded-lg shadow-lg border border-border z-50 py-1">
          {availableRoles.map((roleKey) => {
            const role = PARTICIPANT_TYPES[roleKey];
            if (!role) return null;
            return (
              <button
                key={roleKey}
                onClick={() => handleSwitch(roleKey)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-card-hover transition-colors ${
                  roleKey === activeRole ? 'bg-primary/5 font-medium' : ''
                }`}
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${DOT_COLORS[role.color] || 'bg-gray-500'}`} />
                <div className="text-left min-w-0">
                  <div className="text-foreground truncate">{role.label}</div>
                  <div className="text-xs text-muted truncate">{role.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RoleSelector;
